/**
 * Der Eingang. Hier kommen Auszuege an, werden gelesen und warten auf
 * Pruefung oder Zuordnung. Was exportiert ist, findet man beim Mandanten.
 *
 * Hochgeladen wird per Knopf oder indem man Dateien irgendwo auf die Seite
 * zieht. Mehrere PDFs auf einmal gehen, Fotos eines Papierauszugs werden zu
 * einem Dokument zusammengelegt.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type AuszugKurz, type Eingangsdokument } from '../lib/api'
import { kontoVonAuszug, STAND_TEXT, zeitpunkt, zeitraum } from '../lib/format'
import { MandantWaehlen } from '../teile/MandantWaehlen'
import { Fenster } from '../teile/Fenster'

const spaltenWahl = { gridTemplateColumns: '32px minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 1fr) 90px 130px' }

/** Ein Eintrag in der Auswahl: ein Auszug, oder ein Dokument ohne Auszug (Lesefehler, leer). */
type Wahl = { art: 'auszug'; id: string } | { art: 'dokument'; id: string }
const wahlSchluessel = (w: Wahl) => `${w.art}:${w.id}`

/** Der Kreis am Zeilenanfang. Gefuellt heisst gewaehlt, grau heisst nicht waehlbar. */
function Wahlkreis({ gewaehlt, gesperrt, titel }: { gewaehlt: boolean; gesperrt?: boolean; titel: string }) {
  return (
    <span className={`wahlkreis${gewaehlt ? ' gewaehlt' : ''}${gesperrt ? ' gesperrt' : ''}`} role="checkbox" aria-checked={gewaehlt} aria-disabled={gesperrt} aria-label={titel}>
      {gewaehlt && (
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 6.5 5 9l4.5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

interface Hochladeergebnis {
  ergebnisse: { dateiname: string; dokumentId: string | null; meldung: string | null }[]
  leserEingerichtet: boolean
}

function auszugTitel(a: AuszugKurz): string {
  const art = a.dokumenttyp === 'kreditkartenabrechnung' ? 'Kreditkartenabrechnung' : 'Kontoauszug'
  const nummer = a.auszug_nummer ? `${art} ${a.auszug_nummer}` : art
  const herausgeber = a.bank ?? (a.dokumenttyp === 'kreditkartenabrechnung' ? a.kartenanbieter : null)
  return herausgeber ? `${herausgeber} · ${nummer}` : nummer
}

export function EingangSeite() {
  const abfragen = useQueryClient()
  const [alle, setzeAlle] = useState(false)
  const [zieht, setzeZieht] = useState(false)
  const [laedtHoch, setzeLaedtHoch] = useState(false)
  const [meldungen, setzeMeldungen] = useState<{ art: 'gut' | 'schlecht'; text: string }[]>([])
  const [zuordnen, setzeZuordnen] = useState<AuszugKurz | null>(null)
  const [wahlmodus, setzeWahlmodus] = useState(false)
  const [gewaehlt, setzeGewaehlt] = useState<Map<string, Wahl>>(new Map())
  const [loeschenOffen, setzeLoeschenOffen] = useState(false)
  const [loescht, setzeLoescht] = useState(false)
  // Ein Dokument mit Lesefehler, das schon gelesene Auszuege hat: Verwerfen fragt erst.
  const [verwerfenFuer, setzeVerwerfenFuer] = useState<Eingangsdokument | null>(null)
  const eingabe = useRef<HTMLInputElement>(null)
  const zaehler = useRef(0)

  const { data, isPending, error } = useQuery({
    queryKey: ['eingang', alle],
    queryFn: () => api.holen<{ dokumente: Eingangsdokument[]; leserEingerichtet: boolean }>(`/api/eingang${alle ? '?alle=1' : ''}`),
    // Solange etwas gelesen wird, alle drei Sekunden nachsehen. Sonst nicht.
    refetchInterval: (abfrage) =>
      abfrage.state.data?.dokumente.some((d) => d.stand === 'wartet' || d.stand === 'wird_gelesen') ? 3000 : false,
  })

  const hochladen = useCallback(
    async (dateien: File[]) => {
      if (dateien.length === 0) return
      setzeLaedtHoch(true)
      setzeMeldungen([])
      const formular = new FormData()
      for (const d of dateien) formular.append('datei', d, d.name)
      try {
        const antwort = await api.senden<Hochladeergebnis>('/api/dokumente', formular)
        const neu = antwort.ergebnisse.filter((e) => e.dokumentId && !e.meldung).length
        const liste: { art: 'gut' | 'schlecht'; text: string }[] = []
        if (neu > 0) {
          liste.push({
            art: 'gut',
            text: antwort.leserEingerichtet
              ? `${neu === 1 ? 'Ein Dokument wird' : `${neu} Dokumente werden`} jetzt gelesen. Das dauert je nach Umfang eine bis wenige Minuten.`
              : `${neu === 1 ? 'Ein Dokument ist' : `${neu} Dokumente sind`} abgelegt. Das Lesen ist noch nicht eingerichtet.`,
          })
        }
        for (const e of antwort.ergebnisse.filter((e) => e.meldung)) liste.push({ art: 'schlecht', text: `${e.dateiname}: ${e.meldung}` })
        setzeMeldungen(liste)
        await abfragen.invalidateQueries({ queryKey: ['eingang'] })
      } catch (problem) {
        setzeMeldungen([{ art: 'schlecht', text: problem instanceof ApiFehler ? problem.message : 'Das Hochladen hat nicht geklappt.' }])
      } finally {
        setzeLaedtHoch(false)
      }
    },
    [abfragen],
  )

  /* Ziehen und Ablegen ueber die ganze Seite. Der Zaehler faengt ab, dass
     dragleave auch beim Wechsel zwischen zwei Kindelementen feuert. */
  useEffect(() => {
    const rein = (e: globalThis.DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return
      zaehler.current += 1
      setzeZieht(true)
    }
    const raus = () => {
      zaehler.current = Math.max(0, zaehler.current - 1)
      if (zaehler.current === 0) setzeZieht(false)
    }
    const drueber = (e: globalThis.DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault()
    }
    window.addEventListener('dragenter', rein)
    window.addEventListener('dragleave', raus)
    window.addEventListener('dragover', drueber)
    return () => {
      window.removeEventListener('dragenter', rein)
      window.removeEventListener('dragleave', raus)
      window.removeEventListener('dragover', drueber)
    }
  }, [])

  function ablegen(e: DragEvent) {
    e.preventDefault()
    // Der Schleier liegt im aeusseren Bereich, der dasselbe onDrop hat. Ohne
    // das hier kaeme das Ereignis zweimal an und jede Datei wuerde doppelt hochgeladen.
    e.stopPropagation()
    if (laedtHoch) return
    zaehler.current = 0
    setzeZieht(false)
    void hochladen(Array.from(e.dataTransfer.files))
  }

  async function neuLesen(id: string) {
    try {
      await api.senden(`/api/dokumente/${id}/neu-lesen`)
      await abfragen.invalidateQueries({ queryKey: ['eingang'] })
    } catch (problem) {
      setzeMeldungen([{ art: 'schlecht', text: problem instanceof ApiFehler ? problem.message : 'Das hat nicht geklappt.' }])
    }
  }

  async function verwerfen(id: string) {
    try {
      await api.loeschen(`/api/dokumente/${id}`)
      await abfragen.invalidateQueries({ queryKey: ['eingang'] })
    } catch (problem) {
      setzeMeldungen([{ art: 'schlecht', text: problem instanceof ApiFehler ? problem.message : 'Das hat nicht geklappt.' }])
    }
  }

  const dokumente = data?.dokumente ?? []

  /* ── Auswahl ──
     Waehlbar ist jeder Auszug, der nicht exportiert ist, und jedes Dokument
     ohne Auszug, das nicht gerade gelesen wird. Alles andere bleibt grau. */
  function waehlbar(): Wahl[] {
    const liste: Wahl[] = []
    for (const d of dokumente) {
      if (d.stand !== 'gelesen' || d.auszuege.length === 0) {
        if (d.stand === 'fehler' || d.stand === 'gelesen') liste.push({ art: 'dokument', id: d.id })
      } else {
        for (const a of d.auszuege) if (a.stand !== 'exportiert') liste.push({ art: 'auszug', id: a.id })
      }
    }
    return liste
  }
  function umschalten(w: Wahl) {
    setzeGewaehlt((alt) => {
      const neu = new Map(alt)
      const k = wahlSchluessel(w)
      if (neu.has(k)) neu.delete(k)
      else neu.set(k, w)
      return neu
    })
  }
  function alleWaehlen() {
    setzeGewaehlt(new Map(waehlbar().map((w) => [wahlSchluessel(w), w])))
  }
  function auswahlBeenden() {
    setzeWahlmodus(false)
    setzeGewaehlt(new Map())
  }
  async function gewaehlteLoeschen() {
    setzeLoescht(true)
    const werte = [...gewaehlt.values()]
    try {
      const ergebnis = await api.senden<{ auszuege: number; dokumente: number }>('/api/auszuege/loeschen', {
        auszuege: werte.filter((w) => w.art === 'auszug').map((w) => w.id),
        dokumente: werte.filter((w) => w.art === 'dokument').map((w) => w.id),
      })
      setzeMeldungen([{ art: 'gut', text: `${ergebnis.auszuege === 1 ? 'Ein Auszug' : `${ergebnis.auszuege} Auszüge`} gelöscht${ergebnis.dokumente > 0 ? `, ${ergebnis.dokumente === 1 ? 'ein Original' : `${ergebnis.dokumente} Originale`} mit entfernt` : ''}.` }])
      setzeLoeschenOffen(false)
      auswahlBeenden()
      await abfragen.invalidateQueries()
    } catch (problem) {
      setzeMeldungen([{ art: 'schlecht', text: problem instanceof ApiFehler ? problem.message : 'Das Löschen hat nicht geklappt.' }])
      setzeLoeschenOffen(false)
    } finally {
      setzeLoescht(false)
    }
  }
  const anzahlGewaehlt = gewaehlt.size
  // Die Kreisspalte ist immer da, damit nichts springt.
  const raster = spaltenWahl

  // Leer heisst: nichts da und nichts, das gerade geladen oder gemeldet wird.
  const leer = Boolean(data) && dokumente.length === 0
  const waehlen = () => eingabe.current?.click()

  return (
    <div onDrop={ablegen} onDragOver={(e) => e.preventDefault()}>
      <div className="listenleiste">
        {wahlmodus ? (
          <div className="auswahlleiste" role="group" aria-label="Auswahl">
            {anzahlGewaehlt > 0 && anzahlGewaehlt === waehlbar().length
              ? <button type="button" className="knopf klein" onClick={() => setzeGewaehlt(new Map())}>Keine auswählen</button>
              : <button type="button" className="knopf klein" onClick={alleWaehlen}>Alle auswählen</button>}
            {anzahlGewaehlt > 0 && <span className="leise">{anzahlGewaehlt === 1 ? '1 ausgewählt' : `${anzahlGewaehlt} ausgewählt`}</span>}
          </div>
        ) : (
          <div className="wahl" role="group" aria-label="Anzeige">
            <button type="button" aria-pressed={!alle} onClick={() => setzeAlle(false)}>Offen</button>
            <button type="button" aria-pressed={alle} onClick={() => setzeAlle(true)}>Alle</button>
          </div>
        )}
        <div className="leiste-rechts">
          {!leer && wahlmodus && (
            <>
              <button type="button" className="knopf still loeschknopf" disabled={anzahlGewaehlt === 0} onClick={() => setzeLoeschenOffen(true)}>Löschen</button>
              <button type="button" className="knopf still" onClick={auswahlBeenden}>Fertig</button>
            </>
          )}
          {!leer && !wahlmodus && (
            <button type="button" className="knopf" disabled={laedtHoch} onClick={waehlen}>
              {laedtHoch ? 'Wird hochgeladen …' : 'Auszüge hochladen'}
            </button>
          )}
          <input
            ref={eingabe}
            type="file"
            multiple
            accept="application/pdf,image/jpeg,image/png"
            hidden
            onChange={(e) => {
              void hochladen(Array.from(e.target.files ?? []))
              e.target.value = ''
            }}
          />
        </div>
      </div>

      {data && !data.leserEingerichtet && (
        <div className="fehler abstand-unten">Das Lesen durch die KI ist noch nicht eingerichtet. Hochgeladene Dokumente warten, bis es eingerichtet ist.</div>
      )}

      {meldungen.length > 0 && (
        <div className="meldungen">
          {meldungen.map((m, i) => (
            <div key={i} className={m.art === 'gut' ? 'bestaetigung' : 'fehler'}>{m.text}</div>
          ))}
        </div>
      )}

      {/* Leerer Eingang: nur der eine Knopf, mittig. Ziehen auf die Seite geht trotzdem. */}
      {leer && (
        <div className="leer-mitte">
          <button type="button" className="knopf" disabled={laedtHoch} onClick={waehlen}>
            {laedtHoch ? 'Wird hochgeladen …' : 'Auszüge hochladen'}
          </button>
        </div>
      )}

      {!leer && (
      <div className="liste">
        <div className="listenkopf" style={raster}>
          <div />
          <div>Auszug</div>
          <div>Konto</div>
          <div>Mandant</div>
          <div className="rechts">Buchungen</div>
          <div>Stand</div>
        </div>

        {isPending && <div className="laedt">Wird geladen …</div>}

        {error && (
          <div className="leer">
            <strong>Der Eingang konnte nicht geladen werden.</strong>
            {error instanceof Error ? error.message : ''}
          </div>
        )}


        {dokumente.map((d) => {
          const dokumentzeile = (nurFehler: boolean) => {
            const leer = d.stand === 'gelesen'
            const w: Wahl = { art: 'dokument', id: d.id }
            const waehlbarZeile = d.stand === 'fehler' || leer
            const istGewaehlt = gewaehlt.has(wahlSchluessel(w))
            return (
              <div key={nurFehler ? `${d.id}-fehler` : d.id} className={`zeile mit-kreis${wahlmodus && waehlbarZeile ? ' zeile-wahl' : ''}${istGewaehlt ? ' gewaehlt' : ''}`} style={raster}
                onClick={wahlmodus && waehlbarZeile ? () => umschalten(w) : undefined}>
                <div className={`kreiszelle${waehlbarZeile ? '' : ' gesperrt'}`} onClick={waehlbarZeile ? (e) => { e.stopPropagation(); if (!wahlmodus) setzeWahlmodus(true); umschalten(w) } : undefined}>
                  <Wahlkreis gewaehlt={istGewaehlt} gesperrt={!waehlbarZeile} titel={d.dateiname} />
                </div>
                <div>
                  <div className="dateiname">{d.dateiname}</div>
                  <div className="nebensache">
                    {d.seiten ? `${d.seiten} ${d.seiten === 1 ? 'Seite' : 'Seiten'} · ` : ''}eingegangen {zeitpunkt(d.eingegangen_am)}
                  </div>
                </div>
                <div className="spalte-breit">
                  {d.stand === 'fehler' ? (
                    <span className="text-schlecht">{d.fehler}</span>
                  ) : leer ? (
                    <span className="leiser">Alle Auszüge aus dieser Datei wurden gelöscht.</span>
                  ) : (
                    <span className="leiser">Die KI liest den Auszug und rechnet ihn nach.</span>
                  )}
                </div>
                {(d.stand === 'fehler' || leer) && !wahlmodus ? (
                  /* Zwei Knoepfe brauchen mehr als die Standspalte. */
                  <div className="spalte-aktionen">
                    <div className="zeilenaktionen">
                      <button type="button" className="knopf klein" onClick={() => void neuLesen(d.id)}>Erneut lesen</button>
                      <button type="button" className="knopf still klein" onClick={() => (d.auszuege.length > 0 ? setzeVerwerfenFuer(d) : void verwerfen(d.id))}>Verwerfen</button>
                    </div>
                  </div>
                ) : d.stand === 'fehler' || leer ? (
                  <>
                    <div className="rechts" />
                    <div><span className={`stand ${d.stand === 'fehler' ? 'fehler' : 'wartet'}`}>{d.stand === 'fehler' ? 'Lesefehler' : 'leer'}</span></div>
                  </>
                ) : (
                  /* Wird gelesen: der Stand gehoert unter die Spalte Stand. */
                  <>
                    <div className="rechts" />
                    <div><span className="stand wird_gelesen laeuft">{STAND_TEXT[d.stand]}</span></div>
                  </>
                )}
              </div>
            )
          }
          if (d.stand !== 'gelesen' && d.auszuege.length === 0) return dokumentzeile(false)
          if (d.stand === 'gelesen' && d.auszuege.length === 0) return dokumentzeile(false)
          const auszugzeilen = d.auszuege
            .filter((a) => alle || a.stand !== 'exportiert')
            .map((a, i) => {
              const w: Wahl = { art: 'auszug', id: a.id }
              const waehlbarZeile = a.stand !== 'exportiert'
              const istGewaehlt = gewaehlt.has(wahlSchluessel(w))
              return (
              <div key={a.id} className={`zeile mit-kreis ${wahlmodus ? (waehlbarZeile ? 'zeile-wahl' : '') : 'zeile-link'}${istGewaehlt ? ' gewaehlt' : ''}`} style={raster}
                onClick={wahlmodus && waehlbarZeile ? () => umschalten(w) : undefined}>
                {!wahlmodus && <Link to={`/auszuege/${a.id}`} state={{ von: '/eingang' }} className="zeilenlink" aria-label={`${auszugTitel(a)} öffnen`} />}
                <div className={`kreiszelle ueber-link${waehlbarZeile ? '' : ' gesperrt'}`} onClick={waehlbarZeile ? (e) => { e.stopPropagation(); if (!wahlmodus) setzeWahlmodus(true); umschalten(w) } : undefined}>
                  <Wahlkreis gewaehlt={istGewaehlt} gesperrt={!waehlbarZeile} titel={auszugTitel(a)} />
                </div>
                <div>
                  <div className="sache">{auszugTitel(a)}<span className={`marke${a.dokumenttyp === 'kreditkartenabrechnung' ? ' stark' : ''}`}>{a.dokumenttyp === 'kreditkartenabrechnung' ? 'Karte' : 'Konto'}</span></div>
                  {(d.auszuege.length > 1 || d.hat_textebene === false) && (
                    <div className="nebensache">
                      {[
                        d.auszuege.length > 1 ? `${i + 1}. von ${d.auszuege.length}` : null,
                        d.hat_textebene === false ? 'Scan' : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <div>
                  <div className="leise zahl">{kontoVonAuszug(a)}</div>
                  <div className="nebensache zahl">{zeitraum(a.zeitraum_von, a.zeitraum_bis)}</div>
                </div>
                <div className="ueber-link">
                  {a.mandant_name && a.mandant_id ? (
                    // Fuehrt zum Mandanten, wo exportiert wird. Der Klick darf weder den
                    // Zeilenlink noch die Auswahl der Zeile ausloesen.
                    <Link to={`/mandanten/${a.mandant_id}`} className="leise verweis-still" onClick={(e) => e.stopPropagation()}>{a.mandant_name}</Link>
                  ) : a.stand === 'exportiert' || wahlmodus ? (
                    a.inhaber ? <div className="nebensache">Inhaber: {a.inhaber}</div> : null
                  ) : (
                    <>
                      <button type="button" className="knopf klein" onClick={() => setzeZuordnen(a)}>Mandant zuordnen</button>
                      {a.inhaber && <div className="nebensache">Inhaber: {a.inhaber}</div>}
                    </>
                  )}
                </div>
                <div className="leiser zahl rechts">{a.buchungen}</div>
                <div><span className={`stand ${a.stand}`}>{STAND_TEXT[a.stand]}</span></div>
              </div>
              )
            })
          // Teilausfall: die fertigen Auszuege stehen da, darunter die Fehlerzeile mit "Erneut lesen".
          if (d.stand === 'fehler' || d.stand === 'wartet' || d.stand === 'wird_gelesen') return [...auszugzeilen, dokumentzeile(true)]
          return auszugzeilen
        })}
      </div>
      )}

      {zieht && (
        <div className="ablage-schleier" onDrop={ablegen} onDragOver={(e) => e.preventDefault()}>
          <div className="ablage-rahmen">
            <strong>Loslassen zum Hochladen</strong>
            <span>PDFs werden einzeln gelesen, Fotos zu einem Auszug zusammengelegt.</span>
          </div>
        </div>
      )}

      <Fenster titel={anzahlGewaehlt === 1 ? 'Auszug löschen' : `${anzahlGewaehlt} Auszüge löschen`} offen={loeschenOffen} aufSchliessen={() => { if (!loescht) setzeLoeschenOffen(false) }}>
        <div className="formular">
          <p className="leise">
            {anzahlGewaehlt === 1 ? 'Der gewählte Auszug wird' : `Die ${anzahlGewaehlt} gewählten Auszüge werden`} mit allen Buchungen gelöscht.
            Ein Original, zu dem danach kein Auszug mehr gehört, wird mit entfernt. Exportierte Auszüge sind nicht betroffen.
          </p>
          <div className="formular-fuss">
            <button type="button" className="knopf still" onClick={() => setzeLoeschenOffen(false)} disabled={loescht}>Abbrechen</button>
            <button type="button" className="knopf gefuellt loeschknopf" onClick={() => void gewaehlteLoeschen()} disabled={loescht}>{loescht ? 'Wird gelöscht …' : 'Löschen'}</button>
          </div>
        </div>
      </Fenster>

      <Fenster titel="Dokument verwerfen" offen={verwerfenFuer !== null} aufSchliessen={() => setzeVerwerfenFuer(null)}>
        {verwerfenFuer && (
          <div className="formular">
            <p className="leise">
              Damit wird die Datei <strong>{verwerfenFuer.dateiname}</strong> mit{' '}
              {verwerfenFuer.auszuege.length === 1 ? 'dem bereits gelesenen Auszug' : `allen ${verwerfenFuer.auszuege.length} bereits gelesenen Auszügen`} gelöscht,
              nicht nur der fehlgeschlagene Teil. Wer nur den Rest nachholen will, nimmt „Erneut lesen“.
            </p>
            <div className="formular-fuss">
              <button type="button" className="knopf still" onClick={() => setzeVerwerfenFuer(null)}>Abbrechen</button>
              <button type="button" className="knopf gefuellt loeschknopf" onClick={() => { const id = verwerfenFuer.id; setzeVerwerfenFuer(null); void verwerfen(id) }}>Alles verwerfen</button>
            </div>
          </div>
        )}
      </Fenster>

      {zuordnen && (
        <MandantWaehlen
          offen
          aufSchliessen={() => setzeZuordnen(null)}
          auszugId={zuordnen.id}
          iban={zuordnen.iban}
          inhaber={zuordnen.inhaber}
          karte={zuordnen.dokumenttyp === 'kreditkartenabrechnung' ? { kennung: zuordnen.kontokennung, text: kontoVonAuszug(zuordnen) } : undefined}
        />
      )}
    </div>
  )
}
